/**
 * The "Optimize split…" window of a run's trade mode (TRADE-CONS step 5c; Pedro OPEN_ITEMS TC-S5,
 * every recommendation accepted 2026-11-01).
 *
 * It starts from the consolidation's trade rules (Portfolios → Assign trade rules; the defaults when
 * none are saved) — every value can be changed for this run only (S5-Q4) — and asks the optimizer
 * (`POST /trades/optimize`, S5-Q2: only when the button is pressed) how to split the rows that carry
 * an instruction (S5-Q1: Target % and Quantity / Value / %) across the portfolios. Rows split by hand
 * and transfers typed stay as they are (S5-Q8). The answer is shown first — the trades per security,
 * the transfers between portfolios, what each goal achieved, and anything that could not be done,
 * named (S5-Q7) — and Apply puts it on screen, pending until Save (§3g Pattern A); its transfers
 * between portfolios open in the transfer window, to be checked and confirmed as any other.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Group, Loader, Modal, NumberInput, Select, Stack, Table, Text, TextInput, Tooltip,
} from '@mantine/core';
import { IconAdjustmentsHorizontal } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { tradeRulesApi, withTarget, TARGET_LABEL, type RankingPreset } from '../api/tradeRules';
import { tradesApi, type OptimizeIn, type OptimizeOut, type OptimizerUsed } from '../api/trades';

const errMsg = (e: unknown) => (e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Something went wrong.');
const money = (v: number) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qty = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 6 });
const COUNTS = new Set(['cross_bank', 'transfers', 'trades']);

/** The rules as changed for this run (amounts as typed; min cash in each portfolio's own currency). */
export interface RunRules {
  tolerance_bps: number; preset: RankingPreset; preset_label: string; ranking: string[];
  members: Record<string, { min_cash: number; wrapper: string | null }>;
}
/** problems stop it; warnings do not (e.g. a fund to buy with no price: it will stay off target). */
export interface OptimizeRequest { body: OptimizeIn; problems: string[]; warnings: string[]; free: number }

export function OptimizeWindow({ opened, onClose, consolidationId, members, refCcy, build, rowName, onApply, again, note }: {
  /** TC-S5-FLOW: what this optimizing does not do for the plan on screen (e.g. a model's cash per currency). */
  note?: string | null;
  opened: boolean;
  onClose: () => void;
  consolidationId: string;
  members: { ref: string; label: string; ccy: string; bank: string }[];
  refCcy: string;
  /** The request from what is on screen, with these rules; problems = what stops it. */
  build: (rules: RunRules) => OptimizeRequest;
  rowName: (key: string) => string;
  onApply: (res: OptimizeOut, used: OptimizerUsed) => void;
  /** The rows it split last time are optimized again (their transfers between portfolios replaced). */
  again: boolean;
}) {
  const listQ = useQuery({ queryKey: ['trade-rules', 'list'], queryFn: () => tradeRulesApi.list(), enabled: opened });
  const saved = listQ.data?.items.find((r) => r.consolidation_id === consolidationId) ?? null;
  const [rules, setRules] = useState<RunRules | null>(null);
  const [res, setRes] = useState<OptimizeOut | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The consolidation's saved rules (or the defaults) as this run's starting point, each time it opens.
  useEffect(() => {
    if (!opened) { setRules(null); setRes(null); setError(null); return; }
    if (!listQ.data || rules) return;
    const d = listQ.data;
    const byLink = Object.fromEntries((saved?.members ?? []).map((m) => [m.ref, m]));
    setRules({
      tolerance_bps: Number(saved?.tolerance_bps ?? d.defaults.tolerance_bps),
      preset: saved?.preset ?? d.defaults.preset, preset_label: saved?.preset_label ?? d.preset_labels[d.defaults.preset],
      ranking: saved?.ranking ?? d.presets[d.defaults.preset as Exclude<RankingPreset, 'custom'>],
      members: Object.fromEntries(members.map((m) => [m.ref, { min_cash: Number(byLink[m.ref]?.min_cash ?? 0), wrapper: byLink[m.ref]?.wrapper ?? null }])),
    });
  }, [opened, listQ.data, saved, rules, members]);

  const req = useMemo(() => (rules ? build(rules) : null), [rules, build]);
  const goalLabel = (g: string) => (g === 'target' ? TARGET_LABEL : listQ.data?.goals[g as keyof typeof listQ.data.goals] ?? g);
  const presetOptions = listQ.data ? (Object.keys(listQ.data.preset_labels) as RankingPreset[])
    .filter((p) => p !== 'custom' || saved?.preset === 'custom')
    .map((p) => ({ value: p, label: listQ.data!.preset_labels[p] })) : [];
  const label = (ref: string) => members.find((m) => m.ref === ref)?.label ?? ref;
  const bankOf = (ref: string) => members.find((m) => m.ref === ref)?.bank;

  const change = (patch: Partial<RunRules>) => { setRules((r) => (r ? { ...r, ...patch } : r)); setRes(null); };
  const run = async () => {
    if (!req || !rules) return;
    setBusy(true); setError(null); setRes(null);
    try { setRes(await tradesApi.optimize(req.body)); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  const apply = () => {
    if (!res || !rules) return;
    onApply(res, {
      preset: rules.preset, preset_label: rules.preset_label, ranking: withTarget(rules.ranking),
      tolerance_bps: rules.tolerance_bps, at: new Date().toISOString(),
      members: members.map((m) => ({ label: m.label, min_cash: rules.members[m.ref]?.min_cash ?? 0, ccy: m.ccy,
        wrapper: rules.members[m.ref]?.wrapper ?? null })),
    });
  };

  const bySec = useMemo(() => {
    const out = new Map<string, OptimizeOut['trades']>();
    for (const t of res?.trades ?? []) out.set(t.security, [...(out.get(t.security) ?? []), t]);
    return out;
  }, [res]);

  return (
    <Modal opened={opened} onClose={onClose} size={1000} title={<Group gap="xs"><IconAdjustmentsHorizontal size={18} /><Text fw={700}>Optimize split</Text></Group>}>
      {!rules ? <Group gap="xs"><Loader size="sm" /><Text size="sm">Reading the trade rules…</Text></Group> : (
        <Stack gap="sm">
          <Text size="sm">
            Splits the rows with an instruction across the portfolios, keeping the rules below, with the goals met in this order.
            Rows you split by hand and the transfers you typed stay as they are{again ? '; the rows it split last time are optimized again (the transfers between portfolios it suggested are replaced)' : ''}.
            Nothing changes until you click <b>Apply</b>, and Apply is saved only with the run.
          </Text>
          {note && <Alert color="yellow" py={6}><Text size="xs">{note}</Text></Alert>}
          <Text size="xs" c="dimmed">
            {saved?.saved ? `The consolidation's rules (Assign trade rules), saved by ${saved.updated_by_name ?? '-'}.` : 'No rules saved for this consolidation: the defaults.'}
            {' '}Changes here are for this run only.
          </Text>
          <Group align="flex-start" gap="lg" wrap="nowrap">
            <Stack gap={6} w={330}>
              <NumberInput size="xs" label="Tolerance (bps)" min={0.01} max={1000} decimalScale={2} value={rules.tolerance_bps}
                description={`= ${(rules.tolerance_bps / 100).toFixed(2)} % of the consolidation, per security`}
                onChange={(v) => change({ tolerance_bps: Number(v) || 0 })} />
              <Select size="xs" label="Goal order" data={presetOptions} value={rules.preset} allowDeselect={false}
                onChange={(p) => {
                  if (!p || !listQ.data) return;
                  const preset = p as RankingPreset;
                  change({ preset, preset_label: listQ.data.preset_labels[preset],
                    ranking: preset === 'custom' ? (saved?.ranking ?? rules.ranking) : listQ.data.presets[preset as Exclude<RankingPreset, 'custom'>] });
                }} />
              <Stack gap={0}>
                {withTarget(rules.ranking).map((g, i) => <Text key={g} size="xs">{i + 1}. {goalLabel(g)}</Text>)}
              </Stack>
            </Stack>
            <Table withTableBorder fz="xs" verticalSpacing={2} style={{ flex: 1 }}>
              <Table.Thead><Table.Tr><Table.Th>Portfolio</Table.Th><Table.Th>Minimum cash</Table.Th><Table.Th>Insurance wrapper</Table.Th></Table.Tr></Table.Thead>
              <Table.Tbody>
                {members.map((m) => (
                  <Table.Tr key={m.ref}>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>{m.label}</Table.Td>
                    <Table.Td>
                      <NumberInput size="xs" w={150} min={0} decimalScale={2} thousandSeparator=" " value={rules.members[m.ref]?.min_cash ?? 0}
                        rightSection={<Text size="xs" c="dimmed">{m.ccy}</Text>} rightSectionWidth={40} aria-label={`Minimum cash of ${m.label}`}
                        onChange={(v) => change({ members: { ...rules.members, [m.ref]: { ...rules.members[m.ref], min_cash: Number(v) || 0 } } })} />
                    </Table.Td>
                    <Table.Td>
                      <TextInput size="xs" w={160} placeholder="No wrapper" maxLength={60} value={rules.members[m.ref]?.wrapper ?? ''}
                        aria-label={`Insurance wrapper of ${m.label}`}
                        onChange={(e) => { const v = e.currentTarget.value; change({ members: { ...rules.members, [m.ref]: { ...rules.members[m.ref], wrapper: v || null } } }); }} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Group>

          {req && req.problems.length > 0 && (
            <Alert color="red" py={6} title="Cannot optimize yet">
              <Stack gap={0}>{req.problems.map((p) => <Text key={p} size="xs">{p}</Text>)}</Stack>
            </Alert>
          )}
          {req && req.warnings.length > 0 && (
            <Alert color="orange" py={6} title="Before you optimize">
              <Stack gap={0}>{req.warnings.map((w) => <Text key={w} size="xs">{w}</Text>)}</Stack>
            </Alert>
          )}
          <Group justify="space-between">
            <Text size="xs" c="dimmed">{req ? `${req.free} row${req.free === 1 ? '' : 's'} to split.` : ''} It takes a few seconds; a long one at most about half a minute.</Text>
            <Tooltip withArrow disabled={!!req && req.problems.length === 0 && req.free > 0}
              label={!req ? '' : req.free === 0 ? 'No row carries an instruction (Target %, or an amount with Buy / Sell).' : 'Fix what is listed above first.'}>
              <span><Button onClick={() => void run()} loading={busy} disabled={!req || req.problems.length > 0 || req.free === 0}>Optimize</Button></span>
            </Tooltip>
          </Group>
          {error && <Alert color="red" py={6}>{error}</Alert>}

          {res && (
            <Stack gap="xs">
              <Group gap="xs">
                <Text size="sm" fw={600}>Result</Text>
                <Badge color={res.status === 'optimal' ? 'green' : res.status === 'feasible' ? 'orange' : 'red'} variant="light" style={{ textTransform: 'none' }}>
                  {res.status === 'optimal' ? 'Best split found' : res.status === 'feasible' ? 'Keeps every rule; not proven the best' : 'No split meets the rules'}
                </Badge>
                <Text size="xs" c="dimmed">{res.seconds.toLocaleString()} s</Text>
              </Group>
              {res.exceptions.length > 0 && (
                <Alert color="orange" py={6}>
                  <Stack gap={0}>{res.exceptions.map((e) => <Text key={e} size="xs">{e}</Text>)}</Stack>
                </Alert>
              )}
              {res.status !== 'infeasible' && (<>
                <Table withTableBorder fz="xs" verticalSpacing={2}>
                  <Table.Thead><Table.Tr><Table.Th>Goal, in its order</Table.Th><Table.Th ta="right">Achieved</Table.Th></Table.Tr></Table.Thead>
                  <Table.Tbody>
                    {res.goals.map((g) => (
                      <Table.Tr key={g.goal}>
                        <Table.Td>{goalLabel(g.goal)}</Table.Td>
                        <Table.Td ta="right">{COUNTS.has(g.goal) ? g.value : `${money(g.value)} ${refCcy}`}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                <Table withTableBorder striped fz="xs" verticalSpacing={2}>
                  <Table.Thead><Table.Tr><Table.Th>Security</Table.Th><Table.Th>Side</Table.Th><Table.Th>Per portfolio (quantity)</Table.Th><Table.Th ta="right">Value ({refCcy})</Table.Th></Table.Tr></Table.Thead>
                  <Table.Tbody>
                    {[...bySec.entries()].map(([key, ts]) => (
                      <Table.Tr key={key}>
                        <Table.Td>{rowName(key)}</Table.Td>
                        <Table.Td>{ts[0].side === 'buy' ? 'Buy' : 'Sell'}</Table.Td>
                        <Table.Td>{ts.map((t) => `${label(t.portfolio)}: ${qty(t.quantity)}${t.all ? ' (all)' : ''}`).join(' · ')}</Table.Td>
                        <Table.Td ta="right">{money(ts.reduce((s, t) => s + t.value, 0))}</Table.Td>
                      </Table.Tr>
                    ))}
                    {bySec.size === 0 && <Table.Tr><Table.Td colSpan={4}><Text size="xs" c="dimmed">No trade.</Text></Table.Td></Table.Tr>}
                  </Table.Tbody>
                </Table>
                {res.transfers.length > 0 && (
                  <Stack gap={2}>
                    <Text size="xs" fw={600}>Cash transfers between portfolios ({refCcy}; checked and confirmed in the transfer window on Apply)</Text>
                    {res.transfers.map((t) => (
                      <Text key={`${t.from}>${t.to}`} size="xs">
                        {label(t.from)} → {label(t.to)}: {money(t.amount)}
                        {bankOf(t.from) !== bankOf(t.to) && <Text span size="xs" c="red" fw={600}> · crosses banks</Text>}
                      </Text>
                    ))}
                  </Stack>
                )}
              </>)}
              <Group justify="flex-end">
                <Button variant="default" onClick={onClose}>Back</Button>
                <Button color="orange" disabled={res.status === 'infeasible'} onClick={apply}>Apply</Button>
              </Group>
            </Stack>
          )}
        </Stack>
      )}
    </Modal>
  );
}
